import { parseISO } from 'date-fns/parseISO';
import { toDate } from 'date-fns/toDate';
import JSON5 from 'json5';
import applyTimezoneToDate from './applyTimezoneToDate.js';
import { Buffer as BufferPolyfill } from 'buffer';
import { detectFileType } from '../utils/detectFileType.js';

// Prefer native Buffer for file operations — the 'buffer' import may resolve
// to the npm polyfill in bundled/containerized environments, and the polyfill's
// Buffer.from can overflow the JS call stack on large base64 strings.
const NativeBuffer = globalThis.Buffer || BufferPolyfill;

export const castArgumentDataType = ({ value, dataType, itemDataType, timezone, fileMode, fileMaxSize, component, deferFileHydration = false }) => {
  // Universal transfers validate and decode once at their bounded hydration
  // boundary. Casting here would allocate before that byte budget is checked.
  if (deferFileHydration && (dataType === 'file' || (['array', 'list'].includes(dataType) && itemDataType === 'file'))) {
    return value;
  }

  if (dataType === 'list') {
    dataType = itemDataType || 'string';
  }

  if (value === null || value === 'null') {
    return null;
  }

  const isNonStringPrimitive = ['boolean', 'date', 'number'].includes(dataType);

  if (value === undefined || value === 'undefined' || (isNonStringPrimitive && value === '')) {
    return undefined;
  }

  if (dataType === 'string') {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'function') {
      return value.toString();
    }
    if (typeof value === 'object' && value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'object') {
      return JSON5.stringify(value);
    }
    return String(value);
  }

  if (dataType === 'boolean') {
    if (['true', 't', '1', 'yes'].includes(String(value)?.toLowerCase?.())) {
      return true;
    }
    if (['false', 'f', '0', 'no'].includes(String(value)?.toLowerCase?.())) {
      return false;
    }
    throw new Error('Invalid boolean value');
  }

  if (dataType === 'number') {
    value = Number(value);
    if (isNaN(value)) {
      throw new Error('Invalid number value');
    }
  }

  if (dataType === 'date') {
    if (value instanceof Date && !isNaN(value)) {
      return value;
    }
    let wasIsoString = false;
    if (typeof value === 'string') {
      // Zoned ISO values already identify an instant, regardless of fractional precision.
      // Unzoned editor input keeps the existing Gravity timezone conversion below.
      const wasRealIsoString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:[.,]\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(value);
      const wasESDateString = (str) => Object.prototype.toString.call(new Date(str)) === '[object Date]' && !isNaN(new Date(str).getTime()) && /^\w{3} \w{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4} \(.+\)$/.test(str);
      wasIsoString = wasRealIsoString || wasESDateString(value);

      // Check if string is a unix timestamp (ms or seconds)
      if (/^\d+$/.test(value)) {
        const timestamp = parseInt(value);
        const msTimestamp = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
        value = new Date(msTimestamp);
      } else {
        value = new Date(!isNaN(parseISO(value)) ? parseISO(value) : value.replaceAll('-', ' ').replaceAll('th', ''));
      }
    } else {
      value = toDate(value);
    }

    if (isNaN(value)) {
      throw new Error('Invalid date value');
    }

    if (timezone && !wasIsoString) {
      value = applyTimezoneToDate({ date: value, timezone });
      if (isNaN(value)) {
        throw new Error('Invalid date value');
      }
    }

    return value;
  }

  if (dataType === 'array') {
    if (typeof value === 'string') {
      try {
        value = JSON5.parse(value);
      } catch (error) {
        // If JSON parsing fails, split the string by common delimiters
        value = value.split(/[,|]/).map((item) => item.trim());
      }
    }
    if (Array.isArray(value) && !itemDataType) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((n) => {
        return castArgumentDataType({ value: n, dataType: itemDataType });
      });
    }
    const castValue = castArgumentDataType({ value, dataType: itemDataType });
    return [castValue];
  }

  if (dataType === 'object' && !component) {
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value === 'string') {
      try {
        value = JSON5.parse(value);
        if (typeof value === 'object' && value !== null) {
          return value;
        }
      } catch (error) {
        throw new Error('Invalid JSON');
      }
    }
    throw new Error('Invalid object value');
  }

  if (dataType === 'file') {
    const fileType = detectFileType(value);
    if (fileType === 'url' || fileMode === 'file' || (fileType === 'base64' && fileMode === 'base64')) {
      return value;
    }
    if (typeof value === 'string' && !fileType) {
      try {
        value = JSON5.parse(value);
      } catch (error) {
        throw new Error('Invalid Buffer value');
      }
    }
    if (!NativeBuffer.isBuffer(value)) {
      if (fileType === 'base64') {
        value = NativeBuffer.from(value, 'base64');
      } else if (typeof value === 'object' && value?.type === 'Buffer' && Array.isArray(value.data)) {
        value = NativeBuffer.from(value.data);
      } else {
        value = NativeBuffer.from(value);
      }
    }
    if (fileMaxSize && value.length > fileMaxSize * 1024 * 1024) {
      throw new Error(`File size exceeds maximum allowed size of ${fileMaxSize}MB`);
    }
    if (fileMode === 'base64') {
      return value.toString('base64');
    }
    return value;
  }

  // if this happens, we've got issues.
  return value;
};
